import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Icon,
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
  Button,
  Switch,
} from "../../";
import { useQueryClient } from "@tanstack/react-query";
import { useToggleDriverCompany, useInstallPlugin } from "../../../../hooks";
import { usePostHog, useIntercom } from "@finitless/shared/hooks";
import { useTranslation } from "react-i18next";

const DeliveryServiceCard = ({ driverCompany }) => {
  const { t } = useTranslation("integrations");
  const navigate = useNavigate();
  const { isFeatureEnabled } = usePostHog();
  const { showMessenger } = useIntercom();
  const deliveryServicesConfigureEnabled = isFeatureEnabled(
    "delivery_services_configure_enabled",
  );
  const {
    id,
    name,
    logo,
    enabled,
    plugin_id,
    plugin_root,
    plugin,
    driver_groups,
  } = driverCompany;

  const queryClient = useQueryClient();
  const [isEnabled, setIsEnabled] = useState(enabled);
  const toggleMutation = useToggleDriverCompany();
  const installMutation = useInstallPlugin();

  useEffect(() => {
    setIsEnabled(enabled);
  }, [enabled]);

  const hasPlugin = !!plugin_id;
  const canInstall = !hasPlugin && !!plugin_root;

  const driverGroupId = driver_groups?.[0]?.id;

  const handleToggle = async (checked) => {
    setIsEnabled(checked);
    try {
      await toggleMutation.mutateAsync({
        driverId: id,
        enabled: checked,
      });
    } catch {
      setIsEnabled(!checked);
    }
  };

  const handleCardClick = () => {
    // Use driverGroupId if available, otherwise use the delivery service id
    const navigationId = driverGroupId || id;
    navigate(`/integrations/delivery-services/${navigationId}`, {
      state: {
        plugin,
        isDriverCompanyId: !driverGroupId, // Flag to indicate this is a driverCompanyId, not driverGroupId
        driverCompany: !driverGroupId ? driverCompany : null, // Pass driverCompany data if no driverGroupId
      },
    });
  };

  const handleConfigure = async (e) => {
    e.stopPropagation();
    // Auto-install plugin if not installed
    if (canInstall) {
      const response = await installMutation.mutateAsync({
        pluginRoot: plugin_root,
      });
      const installedPlugin = response?.result;

      // Update driverCompanies cache so the list shows "Installed" when navigating back
      queryClient.setQueriesData(
        { queryKey: ["driverCompanies"] },
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            driverCompanies: oldData.driverCompanies.map((dc) =>
              dc.id === id
                ? {
                    ...dc,
                    plugin_id: installedPlugin?.id,
                    plugin: installedPlugin,
                  }
                : dc,
            ),
          };
        },
      );

      // Navigate with the fresh plugin data
      const updatedCompany = {
        ...driverCompany,
        plugin_id: installedPlugin?.id,
        plugin: installedPlugin,
      };
      const navigationId = driverGroupId || id;
      navigate(`/integrations/delivery-services/${navigationId}`, {
        state: {
          plugin: installedPlugin,
          isDriverCompanyId: !driverGroupId,
          driverCompany: !driverGroupId ? updatedCompany : null,
        },
      });
      return;
    }
    handleCardClick();
  };

  return (
    <div className="ordering-delivery-service-card border-border bg-background flex h-full flex-col rounded-lg border p-6 transition-colors">
      {/* Header */}
      <div className="flex-shrink-0">
        <div className="mb-3 flex items-start justify-between">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            {logo ? (
              <div className="border-border bg-card flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border p-2">
                <img
                  src={logo}
                  alt={name}
                  className="h-full w-full object-contain"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "flex";
                  }}
                />
                <div className="bg-secondary text-foreground flex hidden h-full w-full items-center justify-center text-lg font-semibold">
                  {name.charAt(0)}
                </div>
              </div>
            ) : (
              <div className="border-border bg-card flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-lg border">
                <Icon name="Truck" className="text-muted-foreground h-8 w-8" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h3 className="text-foreground mb-1 text-lg font-semibold break-words">
                {name}
              </h3>
              {!driverGroupId && deliveryServicesConfigureEnabled ? (
                <HoverCard openDelay={300} closeDelay={100}>
                  <HoverCardTrigger asChild>
                    <Button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate("/delivery/groups");
                      }}
                      variant="ghost"
                      className="group border-warning/30 bg-warning/20 text-warning hover:bg-warning/30 inline-flex h-auto max-w-full cursor-pointer items-center space-x-1.5 rounded-full border px-2 py-0.5 text-xs font-medium whitespace-normal transition-colors"
                    >
                      <Icon
                        name="AlertCircle"
                        className="h-3.5 w-3.5 flex-shrink-0"
                      />
                      <span className="underline decoration-dotted underline-offset-2 group-hover:decoration-solid">
                        Delivery Group Required
                      </span>
                    </Button>
                  </HoverCardTrigger>
                  <HoverCardContent
                    side="top"
                    className="border-border bg-card w-72 max-w-[calc(100vw-2rem)] border p-3"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <Icon
                          name="Info"
                          className="text-primary mt-0.5 h-4 w-4 flex-shrink-0"
                        />
                        <div>
                          <p className="text-foreground mb-1 text-sm font-medium">
                            Delivery Group Required
                          </p>
                          <p className="text-foreground text-xs leading-relaxed">
                            This delivery service needs to be configured with a
                            Delivery Group to enable features like assignment
                            settings and business management.
                          </p>
                        </div>
                      </div>
                      <Button
                        emphasis="solid"
                        intent="brand"
                        size="touch"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate("/delivery/groups");
                        }}
                        className="mt-2 flex w-full items-center justify-center px-3 py-1.5"
                      >
                        <Icon name="Settings" className="me-1.5 h-3 w-3" />
                        Configure Delivery Group
                      </Button>
                    </div>
                  </HoverCardContent>
                </HoverCard>
              ) : hasPlugin ? (
                <span className="border-primary/30 bg-primary/20 inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium text-[var(--od-action-content)]">
                  Installed
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="border-border mt-auto flex-shrink-0 space-y-3 border-t pt-4">
        {deliveryServicesConfigureEnabled ? (
          <>
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between">
              <span className="text-foreground text-[length:var(--font-size-product-ui)] font-medium">
                {isEnabled ? "Enabled" : "Disabled"}
              </span>
              <Switch
                aria-label={
                  isEnabled
                    ? t("common:states.enabled")
                    : t("common:states.disabled")
                }
                checked={isEnabled}
                onCheckedChange={handleToggle}
                disabled={toggleMutation.isPending}
              />
            </div>

            {/* Configure Button */}
            <Button
              type="button"
              onClick={handleConfigure}
              disabled={installMutation.isPending}
              emphasis="solid"
              intent="brand"
              size="touch"
              className="w-full"
            >
              {installMutation.isPending ? (
                <>
                  <div className="me-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                  <span>Installing...</span>
                </>
              ) : (
                <span>{t("actions.configure")}</span>
              )}
            </Button>
          </>
        ) : (
          <Button
            onClick={() => showMessenger()}
            emphasis="solid"
            intent="neutral"
            size="touch"
            className="w-full"
          >
            <Icon name="MessageCircle" className="me-2 h-4 w-4" />
            <span>{t("contactUs")}</span>
          </Button>
        )}
      </div>
    </div>
  );
};

export default DeliveryServiceCard;
